import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { ProductType } from '@xing-com/crate-common-graphql-types';
import { useSixtFocusmode } from '@xing-com/crate-layout-use-sixt-focusmode';
import { useFeatures, useFeatureSwitch } from '@xing-com/hub';
import {
  IconBookmark,
  IconBookmarkFilled,
  IconContacts,
  IconContactsFilled,
  IconJobs,
  IconJobsFilled,
  IconMessages,
  IconMessagesFilled,
  IconNews,
  IconNewsFilled,
  IconNotificationFilled,
} from '@xing-com/icons';
import { ProductsMenuWithItemsLoggedIn } from '@xing-com/platform-layout-products-menu';

import { DiscountBadge } from './discount-badge';
import {
  StyledProductsMenuWrapper,
  BoneyardWithDiscountContainer,
} from './header.styles';
import { MeMenuDropdown } from './me-menu-dropdown';
import { NavItemHeader } from './nav-item-header';
import { NotificationCenterIcon } from './notification-center-icon';
import { SearchDropdown } from './search-dropdown';
import { SixtFocusmodeToggle } from './sixt-focusmode-toggle';

const NAVIGATION_BADGE_NO_BADGE = 'navigation_badge_no_badge';

const StyledUpsellButton = styled(Button)`
  white-space: nowrap;
`;

export const HeaderNavItemsLoggedIn: React.FC = () => {
  const { formatMessage } = useIntl();

  const { showUpsellHint, showJobSeekerBoneyardUpsellHint } =
    useFeatures()?.data || {};

  const jobsEasyCreateInBoneyardEnabled = useFeatureSwitch(
    'jobs_easy_create_in_boneyard',
    false
  );
  const { isSixtFocusmodeAvailable, isSixtFocusmodeEnabled } =
    useSixtFocusmode();

  return (
    <>
      <NavItemHeader
        activeItemValues={['jobs']}
        icon={IconJobs}
        activeIcon={IconJobsFilled}
        title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
        href="/jobs/find"
        tracking={{
          nwt_nav: 'find_jobs',
          sc_o: 'navigation_find_jobs',
        }}
      />
      <NavItemHeader
        activeItemValues={['my-jobs']}
        icon={IconBookmark}
        activeIcon={IconBookmarkFilled}
        title={formatMessage({ id: 'MAINNAV_MYJOBS' })}
        href="/jobs/my-jobs"
        tracking={{
          nwt_nav: 'your_jobs',
          sc_o: 'navigation_your_jobs',
          sc_o_PropActionOrigin: [
            'navigation_badge_jobs',
            NAVIGATION_BADGE_NO_BADGE,
          ],
        }}
      />
      {isSixtFocusmodeEnabled ? null : (
        <NavItemHeader
          icon={IconContacts}
          activeIcon={IconContactsFilled}
          title={formatMessage({ id: 'MAINNAV_NETWORK' })}
          href="/network"
          activeItemValues={['members']}
          tracking={{
            nwt_nav: 'network',
            sc_o: 'navigation_network',
            sc_o_PropActionOrigin: [
              'navigation_badge_network',
              NAVIGATION_BADGE_NO_BADGE,
            ],
          }}
        />
      )}
      {isSixtFocusmodeEnabled ? null : (
        <NavItemHeader
          icon={IconNews}
          activeIcon={IconNewsFilled}
          title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
          href="/discover/updates"
          activeItemValues={['disco']}
          tracking={{
            nwt_nav: 'insights',
            sc_o: 'navigation_insights',
            sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
          }}
        />
      )}
      <NavItemHeader
        icon={IconMessages}
        activeIcon={IconMessagesFilled}
        title={formatMessage({ id: 'MAINNAV_MESSAGES' })}
        href="/chats"
        activeItemValues={['messages']}
        notificationKey="unreadChats"
        tracking={{
          nwt_nav: 'messages',
          sc_o: 'navigation_messages',
          sc_o_PropActionOrigin: [
            'navigation_badge_messages',
            NAVIGATION_BADGE_NO_BADGE,
          ],
        }}
      />
      <NavItemHeader
        icon={NotificationCenterIcon}
        activeIcon={IconNotificationFilled}
        provideIconContext
        activeItemValues={['supi']}
        notificationKey="ncWeb"
        href="/notifications"
        title={formatMessage({ id: 'NOTIFICATION_CENTER_LINK' })}
        tracking={{
          nwt_nav: 'notifications',
          sc_o: 'navigation_notifications',
          sc_o_PropActionOrigin: [
            'navigation_badge_notifications',
            NAVIGATION_BADGE_NO_BADGE,
          ],
        }}
      />
      {isSixtFocusmodeEnabled ? null : <SearchDropdown />}
      <MeMenuDropdown />
      {isSixtFocusmodeEnabled ? null : !showUpsellHint ? null : jobsEasyCreateInBoneyardEnabled ? (
        <Button
          variant="primary"
          size="small"
          data-testid="job-easy-create-button"
          href="/recruiting/jobs/create?nwt_nav=job_ad&sc_cmp=oowcbc0e3c5"
        >
          {formatMessage({
            id: 'MAINNAV_JOBS_EASY_CREATE',
          })}
        </Button>
      ) : showJobSeekerBoneyardUpsellHint ? (
        <BoneyardWithDiscountContainer>
          <StyledUpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/projobs?reagent=uplt_277&sc_o=navigation_projobs_cta"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PROJOBS',
            })}
          </StyledUpsellButton>

          <DiscountBadge product={ProductType.Projobs} />
        </BoneyardWithDiscountContainer>
      ) : (
        <BoneyardWithDiscountContainer>
          <StyledUpsellButton
            data-testid="upsell-button"
            size="small"
            variant="primary"
            href="/purchase/premium?reagent=uplt_95&sc_o=navigation_premium_cta"
          >
            {formatMessage({
              id: 'FRAME_HEADER_UPSELL_PREMIUM',
            })}
          </StyledUpsellButton>
          <DiscountBadge product={ProductType.Premium} />
        </BoneyardWithDiscountContainer>
      )}

      {isSixtFocusmodeEnabled ? null : (
        <StyledProductsMenuWrapper>
          <ProductsMenuWithItemsLoggedIn />
        </StyledProductsMenuWrapper>
      )}

      {isSixtFocusmodeAvailable && <SixtFocusmodeToggle />}
    </>
  );
};
